import { GenericItem, GetItemKey, ItemToString } from '../types';
import areItemsEqual from './areItemsEqual';

/**
 * Default implementation of getItemKey that tries common key properties
 * and falls back to itemToString.
 */
export const createDefaultGetItemKey = <TItem extends GenericItem>(
  itemToString: ItemToString<TItem>,
): GetItemKey<TItem> => {
  return (item: TItem): string => {
    if ('key' in item && typeof item.key === 'string') return item.key;
    if (
      'id' in item &&
      (typeof item.id === 'string' || typeof item.id === 'number')
    ) {
      return String(item.id);
    }
    if (
      'value' in item &&
      (typeof item.value === 'string' || typeof item.value === 'number')
    ) {
      return String(item.value);
    }
    return itemToString(item);
  };
};

/**
 * Check if an array includes an item using key-based equality.
 * When `getItemKey` is not provided, uses reference equality (`===`).
 * When `getItemKey` is provided, compares items by their keys.
 *
 * @param array - Array to search in
 * @param item - Item to find
 * @param getItemKey - Optional function to extract unique key. If not provided, uses reference equality (`===`)
 * @returns true if item is found in array
 */
export const includesItem = <TItem extends GenericItem>(
  array: TItem[],
  item: TItem,
  getItemKey?: GetItemKey<TItem>,
): boolean => {
  if (!getItemKey) {
    return array.includes(item);
  }

  const itemToKey = (i: TItem | null | undefined) =>
    i ? getItemKey(i) : undefined;

  return array.some((arrayItem) => areItemsEqual(arrayItem, item, itemToKey));
};

/**
 * Build a Set of item keys for O(1) membership checks when the same array
 * is queried many times (e.g. inside a filter over another list).
 * Use when getItemKey is available; for reference equality, use includesItem.
 *
 * @param array - Array of items to index by key
 * @param getItemKey - Function to extract unique key from each item
 * @returns Set of keys for O(1) lookup via set.has(getItemKey(item))
 */
export const createItemKeySet = <TItem extends GenericItem>(
  array: TItem[],
  getItemKey: GetItemKey<TItem>,
): Set<string> => {
  const set = new Set<string>();
  for (const item of array) {
    if (item !== null) {
      set.add(getItemKey(item));
    }
  }
  return set;
};

/**
 * Find the index of an item in an array using key-based equality.
 */
export const indexOfItem = <TItem extends GenericItem>(
  array: TItem[],
  item: TItem,
  getItemKey?: GetItemKey<TItem>,
): number => {
  if (!getItemKey) {
    return array.indexOf(item);
  }

  const itemToKey = (i: TItem | null | undefined) =>
    i ? getItemKey(i) : undefined;

  return array.findIndex((arrayItem) =>
    areItemsEqual(arrayItem, item, itemToKey),
  );
};

/**
 * Remove an item from an array using key-based equality.
 * Returns a new array without the item.
 */
export const removeItem = <TItem extends GenericItem>(
  array: TItem[],
  item: TItem,
  getItemKey?: GetItemKey<TItem>,
): TItem[] => {
  const index = indexOfItem(array, item, getItemKey);
  if (index === -1) {
    return array;
  }
  return [...array.slice(0, index), ...array.slice(index + 1)];
};

/**
 * Deduplicate an array of items using key-based equality.
 * Returns a new array with only unique items (keeps first occurrence).
 */
export const deduplicateItems = <TItem extends GenericItem>(
  items: TItem[],
  getItemKey?: GetItemKey<TItem>,
): TItem[] => {
  if (!getItemKey) {
    return Array.from(new Set(items));
  }

  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getItemKey(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
